import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NO_AUTH_CONTEXT } from '@app/tokens';
import { SharedAuthSignInChallenge } from '@libs/shared/domain/auth/sign-in.challenge.';
import { SharedAuthSignInResponseSchema } from '@libs/shared/domain/auth/sign-in.response.schema';
import { AuthStorage } from '@services/auth.storage';
import { UrlService } from '@services/url.service';
import { UserRoleModel } from '@shared/models/user-role';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export type SignInSuccess = SharedAuthSignInResponseSchema & { refreshToken: string; challenge?: null };
export type SignInError = { challenge: SharedAuthSignInChallenge; meta?: { mfaType?: 'email' | 'sms' } };
export type SignInResponse = SignInSuccess | SignInError;
export type AuthChangeResult = { id: string };

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private httpClient = inject(HttpClient);
  private urlService = inject(UrlService);

  signIn(username: string, password: string, challenge?: SharedAuthSignInChallenge, response?: string): Observable<SignInResponse> {
    return this.httpClient
      .post<SharedAuthSignInResponseSchema>(
        `${this.urlService.AUTH_SERVICE_URL}/sign-in`,
        { username, password, challenge, challengeResponse: response },
        { context: NO_AUTH_CONTEXT, observe: 'response' },
      )
      .pipe(
        map(response => {
          const refreshToken = response.headers.get('Refresh-Token') ?? '';
          return { ...response.body, refreshToken } as SignInResponse;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.error?.challenge) {
            return of(error.error as SignInError);
          }
          return throwError(() => error);
        }),
      );
  }

  refreshToken(token: string, cognitoToken: string): Observable<SharedAuthSignInResponseSchema> {
    return this.httpClient.post<SharedAuthSignInResponseSchema>(
      `${this.urlService.AUTH_SERVICE_URL}/refresh-token`,
      { cognitoToken },
      { context: NO_AUTH_CONTEXT, headers: { 'Refresh-Token': token } },
    );
  }

  signOut(token: string, cognitoToken: string) {
    return this.httpClient.post<object>(
      `${this.urlService.AUTH_SERVICE_URL}/sign-out`,
      { cognitoToken },
      { context: NO_AUTH_CONTEXT, headers: { 'Refresh-Token': token } },
    );
  }

  forgotPassword(username: string) {
    const url = `${this.urlService.AUTH_SERVICE_URL}/forgot-password`;
    const body = { username };
    return this.httpClient.post<AuthChangeResult>(url, body, { context: NO_AUTH_CONTEXT });
  }

  resetPassword(token: string, password: string) {
    const url = `${this.urlService.AUTH_SERVICE_URL}/confirm-forgot-password`;
    const body = { token, password };
    return this.httpClient.post<AuthChangeResult>(url, body, { context: NO_AUTH_CONTEXT });
  }

  changePassword(oldPassword: string, newPassword: string) {
    const url = `${this.urlService.AUTH_SERVICE_URL}/change-password`;
    const body = { oldPassword, newPassword };
    return this.httpClient.post<AuthChangeResult>(url, body);
  }

  getUserRole(roleId: string) {
    return this.httpClient
      .get<UserRoleModel | null>(`${this.urlService.USERS_ROLES_URL}/${roleId}`)
      .pipe(tap(role => localStorage.setItem(AuthStorage.ROLE, JSON.stringify(role))));
  }
}
